import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { apiService, PaymentAccount } from '../services/api';

type CurrencyGroup = 'SEK' | 'EUR' | 'OTHER';

const ACCOUNT_SLOTS: { group: CurrencyGroup; label: string; description: string }[] = [
  { group: 'SEK', label: 'SEK Account', description: 'Used for domestic Swedish payments (ACH CT — Bankgiro, Payroll, local account)' },
  { group: 'EUR', label: 'EUR Account', description: 'Used for SEPA CT, CBFT and DFT payments' },
];

interface AccountForm {
  account_number: string;
  company_name: string;
  bic: string;
}

type FieldErrors = Partial<Record<keyof AccountForm, string>>;
type AllFieldErrors = Record<CurrencyGroup, FieldErrors>;

const EMPTY_FORM: AccountForm = { account_number: '', company_name: '', bic: '' };
const EMPTY_ERRORS: AllFieldErrors = { SEK: {}, EUR: {}, OTHER: {} };

function validateAll(forms: Record<CurrencyGroup, AccountForm>): AllFieldErrors {
  const errs: AllFieldErrors = { SEK: {}, EUR: {}, OTHER: {} };
  for (const { group } of ACCOUNT_SLOTS) {
    const f = forms[group];
    if (!f.account_number.trim()) errs[group].account_number = 'Required';
    if (!f.company_name.trim()) errs[group].company_name = 'Required';
    else if (f.company_name.trim().length > 35) errs[group].company_name = 'Must be 35 characters or fewer';
    if (!f.bic.trim()) errs[group].bic = 'Required';
    else if (f.bic.trim().length !== 8 && f.bic.trim().length !== 11) errs[group].bic = 'Must be 8 or 11 characters';
  }
  return errs;
}

function hasAnyError(errs: AllFieldErrors) {
  return ACCOUNT_SLOTS.some(({ group }) => Object.keys(errs[group]).length > 0);
}

function parseBackendFieldError(message: string): keyof AccountForm | null {
  if (message.includes('account_number')) return 'account_number';
  if (message.includes('company_name')) return 'company_name';
  if (message.includes('bic')) return 'bic';
  return null;
}

interface AccountSectionProps {
  group: CurrencyGroup;
  label: string;
  description: string;
  form: AccountForm;
  fe: FieldErrors;
  onChange: (group: CurrencyGroup, field: keyof AccountForm, value: string) => void;
}

function AccountSection({ group, label, description, form, fe, onChange }: AccountSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="mb-2">
        <h2 className="text-sm font-medium text-foreground">{label}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>

      <div>
        <label className="block text-xs font-medium text-foreground mb-1">Account Number</label>
        <input
          type="text"
          value={form.account_number}
          onChange={(e) => onChange(group, 'account_number', e.target.value)}
          placeholder="e.g. 1234567890"
          className={inputClass(!!fe.account_number)}
        />
        {fe.account_number && <p className="text-xs text-destructive mt-1">{fe.account_number}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-foreground mb-1">
          Company Name
          <span className="text-muted-foreground font-normal ml-1">(max 35 chars)</span>
        </label>
        <input
          type="text"
          value={form.company_name}
          onChange={(e) => onChange(group, 'company_name', e.target.value)}
          maxLength={35}
          placeholder="e.g. Another Snus Factory"
          className={inputClass(!!fe.company_name)}
        />
        {fe.company_name && <p className="text-xs text-destructive mt-1">{fe.company_name}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-foreground mb-1">
          BIC
          <span className="text-muted-foreground font-normal ml-1">(8 or 11 chars)</span>
        </label>
        <input
          type="text"
          value={form.bic}
          onChange={(e) => onChange(group, 'bic', e.target.value.toUpperCase())}
          placeholder="e.g. CITISESX"
          className={inputClass(!!fe.bic) + ' font-mono'}
        />
        {fe.bic && <p className="text-xs text-destructive mt-1">{fe.bic}</p>}
      </div>

    </div>
  );
}

function inputClass(hasErr: boolean) {
  return `w-full h-9 px-3 text-sm border rounded-md bg-background focus:outline-none focus:ring-1 ${
    hasErr
      ? 'border-destructive focus:ring-destructive/30 text-destructive'
      : 'border-input focus:ring-ring'
  }`;
}

export function Settings() {
  const [accounts, setAccounts] = useState<Partial<Record<CurrencyGroup, PaymentAccount>>>({});
  const [forms, setForms] = useState<Record<CurrencyGroup, AccountForm>>({
    SEK: { ...EMPTY_FORM },
    EUR: { ...EMPTY_FORM },
    OTHER: { ...EMPTY_FORM },
  });
  const [fieldErrors, setFieldErrors] = useState<AllFieldErrors>({ ...EMPTY_ERRORS });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.getPaymentAccounts().then(({ accounts: rows }) => {
      const byGroup: Partial<Record<CurrencyGroup, PaymentAccount>> = {};
      const initialForms = { SEK: { ...EMPTY_FORM }, EUR: { ...EMPTY_FORM }, OTHER: { ...EMPTY_FORM } };
      for (const row of rows) {
        byGroup[row.currency_group] = row;
        initialForms[row.currency_group] = {
          account_number: row.account_number,
          company_name: row.company_name,
          bic: row.bic,
        };
      }
      setAccounts(byGroup);
      setForms(initialForms);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function handleChange(group: CurrencyGroup, field: keyof AccountForm, value: string) {
    setForms((prev) => ({ ...prev, [group]: { ...prev[group], [field]: value } }));
    setFieldErrors((prev) => ({ ...prev, [group]: { ...prev[group], [field]: undefined } }));
    setError(undefined);
    setSaved(false);
  }

  async function handleSave() {
    const validationErrors = validateAll(forms);
    if (hasAnyError(validationErrors)) {
      setFieldErrors(validationErrors);
      return;
    }

    setSaving(true);
    setFieldErrors({ ...EMPTY_ERRORS });
    setError(undefined);
    setSaved(false);

    const results = await Promise.allSettled(
      ACCOUNT_SLOTS.map(({ group }) => apiService.savePaymentAccount(group, forms[group]))
    );

    const newFieldErrors: AllFieldErrors = { SEK: {}, EUR: {}, OTHER: {} };
    let anyFailed = false;

    results.forEach((result, i) => {
      const { group } = ACCOUNT_SLOTS[i];
      if (result.status === 'fulfilled') {
        setAccounts((prev) => ({ ...prev, [group]: result.value.account }));
      } else {
        anyFailed = true;
        const message = result.reason instanceof Error ? result.reason.message : 'Save failed';
        const field = parseBackendFieldError(message);
        if (field) {
          newFieldErrors[group][field] = message;
        } else {
          setError(message);
        }
      }
    });

    setFieldErrors(newFieldErrors);
    if (!anyFailed) setSaved(true);
    setSaving(false);
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure the debtor accounts used to send supplier invoice payments.
          </p>
        </div>

        {loading ? (
          <div className="bg-white border border-border/60 rounded-xl p-8">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <div className="bg-white border border-border/60 rounded-xl">
            <div className="p-8">
              <div className="grid grid-cols-2 gap-x-10">
                {ACCOUNT_SLOTS.map(({ group, label, description }) => (
                  <AccountSection
                    key={group}
                    group={group}
                    label={label}
                    description={description}
                    form={forms[group]}
                    fe={fieldErrors[group]}
                    onChange={handleChange}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between px-8 py-5 border-t border-border/60">
              <div>
                {error && <p className="text-xs text-destructive">{error}</p>}
                {saved && !error && <p className="text-xs text-green-600">Settings saved</p>}
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 text-sm bg-foreground text-background rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
